import React from "react";
import type { DecisionExplanationView } from "@/api";
import { useLang, useT, type Lang } from "../../i18n";
import styles from "./decision-explanation.module.css";

const RELATIONS = new Set(["at_or_above", "below", "count_unknown", "not_recorded"]);
const RULE_VALUE_SOURCES = new Set(["business", "defaults", "unverified"]);
const QUESTION_TOPICS = new Set(["event", "food", "logistics", "other"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isNumberOrNull = (value: unknown) => value === null || typeof value === "number";
const isStringOrNull = (value: unknown) => value === null || typeof value === "string";
const isQuestion = (question: unknown) => isRecord(question)
  && typeof question.id === "string" && isStringOrNull(question.meaning) && isStringOrNull(question.rule_id);

/** The explanation is Core's; a shape this build does not know hides the panel instead of guessing. */
export function readDecisionExplanation(value: unknown): DecisionExplanationView | null {
  if (!isRecord(value) || typeof value.ruleset_version !== "string" || typeof value.response_type !== "string") return null;
  const threshold = value.guest_threshold;
  if (!isRecord(threshold) || !RELATIONS.has(threshold.relation as string)
    || !isNumberOrNull(threshold.min_written_offer_guests)) return null;
  const ruleValues = value.rule_values;
  if (!isRecord(ruleValues) || !RULE_VALUE_SOURCES.has(ruleValues.source as string)
    || ruleValues.source === "business" && typeof ruleValues.version !== "number") return null;
  const commercial = value.commercial;
  if (commercial !== null && (!isRecord(commercial) || typeof commercial.outcome !== "string"
    || typeof commercial.reason_code !== "string" || !isNumberOrNull(commercial.threshold_net_eur)
    || !isNumberOrNull(commercial.food_subtotal_net_eur))) return null;
  if (!Array.isArray(value.questions) || !value.questions.every(isQuestion)) return null;
  if (value.question_groups !== undefined && (!Array.isArray(value.question_groups)
    || !value.question_groups.every((group) => isRecord(group)
      && (group.topic === null || QUESTION_TOPICS.has(group.topic as string))
      && Array.isArray(group.questions) && group.questions.every(isQuestion)))) return null;
  if (!Array.isArray(value.review_reasons) || !value.review_reasons.every((reason) => isRecord(reason)
    && typeof reason.code === "string" && typeof reason.text === "string")) return null;
  return value as unknown as DecisionExplanationView;
}

function euro(value: number, lang: Lang): string {
  return value.toLocaleString(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: "EUR" });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>;
}

/** #1684: why the draft looks as it does. It renders what Core recorded and decides nothing. */
export function DecisionExplanation({ explanation: raw }: { explanation: unknown }) {
  const t = useT();
  const { lang } = useLang();
  const explanation = readDecisionExplanation(raw);
  if (!explanation) return null;
  const { guest_threshold: threshold, rule_values: ruleValues, commercial } = explanation;
  const questionGroups = explanation.question_groups ?? [{ topic: null, questions: explanation.questions }];
  return (
    <details open={explanation.review_reasons.some((reason) => reason.code === "EARLY_LATE_ENQUIRY") || undefined} className={styles.panel} data-testid="decision-explanation">
      <summary>{t("ws.explain.title")}</summary>
      <dl className={styles.facts}>
        <Row label={t("ws.explain.outcome")}>
          {t(`status.response.${explanation.response_type}`, undefined, explanation.response_type)}
        </Row>
        <Row label={t("ws.explain.guestCount")}>{t(`ws.explain.relation.${threshold.relation}`)}</Row>
        <Row label={t("ws.explain.guestThreshold")}>
          {threshold.min_written_offer_guests === null
            ? t("ws.explain.notProvable")
            : t("ws.explain.guests", { n: String(threshold.min_written_offer_guests) })}
        </Row>
        <Row label={t("ws.explain.ruleValues")}>
          {ruleValues.source === "business"
            ? t("ws.explain.ruleValuesVersion", { v: String(ruleValues.version) })
            : t(`ws.explain.ruleValues.${ruleValues.source}`)}
        </Row>
        {commercial && (
          <Row label={t("ws.explain.commercial")}>
            {t(`ws.explain.commercial.${commercial.outcome}`, undefined, commercial.outcome)}
            {commercial.food_subtotal_net_eur !== null && commercial.threshold_net_eur !== null && (
              <> · {t("ws.explain.foodMinimum", {
                subtotal: euro(commercial.food_subtotal_net_eur, lang),
                threshold: euro(commercial.threshold_net_eur, lang),
              })}</>
            )}
            {" "}<span className={styles.code}>{commercial.reason_code}</span>
          </Row>
        )}
        <Row label={t("ws.explain.ruleset")}><span className={styles.code}>{explanation.ruleset_version}</span></Row>
      </dl>
      <section className={styles.section}>
        <h4>{t("ws.explain.questions")}</h4>
        {explanation.questions.length === 0
          ? <p className={styles.none}>{t("ws.explain.none")}</p>
          : (
            questionGroups.map((group, index) => <div key={group.topic ?? index}>
              {group.topic && <h5 className={styles.topic}>{t(`ws.explain.topic.${group.topic}`)}</h5>}
              <ul>
              {group.questions.map((question) => (
                <li key={question.id}>
                  <span>{question.meaning ?? question.id}</span>
                  <span className={styles.code}>
                    {question.id} · {question.rule_id
                      ? t("ws.explain.rule", { id: question.rule_id })
                      : t("ws.explain.ruleNotRecorded")}
                  </span>
                </li>
              ))}
              </ul>
            </div>)
          )}
      </section>
      <section className={styles.section}>
        <h4>{t("ws.explain.reviewReasons")}</h4>
        {explanation.review_reasons.length === 0
          ? <p className={styles.none}>{t("ws.explain.none")}</p>
          : (
            <ul>
              {explanation.review_reasons.map((reason, index) => (
                <li key={`${reason.code}:${index}`}>
                  <span>{reason.code === "EARLY_LATE_ENQUIRY" ? t("ws.explain.earlyLateEnquiry") : reason.text}</span>
                  <span className={styles.code}>{reason.code}</span>
                </li>
              ))}
            </ul>
          )}
      </section>
    </details>
  );
}
